"use client";
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { onSmoothScroll } from "@/Components/common/SmoothScroll";
import Image from "next/image";
import dynamic from "next/dynamic";
const PromisesMobile = dynamic(() => import("./promises-mobile"), { ssr: true });
import { parseHtml, toWebp } from "@/utils/common";
import styles from "./styles.module.scss";

gsap.registerPlugin(ScrollTrigger);

interface CareerPromisesProps {
  data?: any;
  isKill?: boolean;
}

const CareerPromises = ({ data, isKill }: CareerPromisesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";

  useEffect(() => {
    if (!containerRef.current) return;

    if (isKill) {
      gsap.killTweensOf(containerRef.current);
      ScrollTrigger.getAll().forEach((st) => st.kill()); // ✅ cleanup
      return;
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        pin: true,
        pinSpacing: true,
        start: "top-=100px top",
        end: "+=3000", // adjust scroll distance
        // Lenis already eases the scroll; a numeric scrub would stack a second lag on top
        scrub: true,
        markers: false,
      },
    });

    // Card animations
    tl.addLabel("card1").to(".card1", { yPercent: 0, opacity: 1 });

    tl.from(".card2", { yPercent: 75, opacity: 0 })
      .addLabel("card2")
      .to(".card1", { scale: 1, yPercent: -0.75, opacity: 1 }, "-=1")
      .to(".card2", { yPercent: 0, opacity: 1 });

    tl.from(".card3", { yPercent: 75, opacity: 0 })
      .addLabel("card3")
      .to(".card2", { scale: 1, yPercent: -0.5, opacity: 1 }, "-=1")
      .to(".card3", { yPercent: 0, opacity: 1 });

    tl.from(".card4", { yPercent: 75, opacity: 0 })
      .addLabel("card4")
      .to(".card3", { scale: 1, yPercent: -0.4, opacity: 1 }, "-=1")
      .to(".card4", { yPercent: 0, opacity: 1 });

    tl.from(".card5", { yPercent: 75, opacity: 0 })
      .addLabel("card5")
      .to(".card4", { scale: 1, yPercent: -0.3, opacity: 1 }, "-=1")
      .to(".card5", { yPercent: 0, opacity: 1 });

    tl.from(".card6", { yPercent: 75, opacity: 0 })
      .addLabel("card6")
      .to(".card5", { scale: 1, yPercent: -0.2, opacity: 1 }, "-=1")
      .to(".card6", { yPercent: 0, opacity: 1 });

    // Progressive scale down
    tl.to(".card1", { scale: 1, yPercent: -1.8, opacity: 1 }, "-=1")
      .to(".card2", { scale: 1, yPercent: -1.4, opacity: 1 }, "-=1")
      .to(".card3", { scale: 1, yPercent: -1.0, opacity: 1 }, "-=1")
      .to(".card4", { scale: 1, yPercent: -0.6, opacity: 1 }, "-=1")
      .to(".card5", { scale: 1, yPercent: -0.3, opacity: 1 }, "-=1")
      .to(".card6", { scale: 1, yPercent: -0.8, opacity: 1 }, "-=1");

    // return () => {
    //   tl.kill(); // ✅ kill timeline on unmount
    //   ScrollTrigger.getAll().forEach((st) => st.kill());
    // };
    return onSmoothScroll(ScrollTrigger.update);
  }, [isKill]);

  return (
    <section
      className={`main-section relative z-[0] py-[56px] lg:py-[80px] xl:py-[100px] bg-[#040405] 
    before:content-[''] before:absolute before:h-[100%] before:w-[100%] before:right-0 before:top-0 
    before:bg-[url('/images/career-bg-shape.png')] before:bg-no-repeat before:bg-[length:100%_100%] 
    before:bg-center before:-z-[1] ${styles.mainSectionCareer}`}
    >
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-[calc(30%_-_12px)_calc(70%_-_12px)] xl:grid-cols-[calc(40%_-_15px)_calc(60%_-_15px)] gap-[24px] xl:gap-[30px]">
          {/* Left Section */}
          <div className="relative">
            <div className="mb-[32px] lg:mb-[32px] xl:mb-[40px] sticky top-[100px]">
              <h2 className="md:text-left text-center font-bold  text-heading-1 text-white mb-[16px]">
                {data?.Title}
              </h2>
              <p className="text-white text-subtitle-3 md:text-left text-center">
                {data?.Description}
              </p>
            </div>
          </div>

          {/* Desktop Cards */}
          <div
            ref={containerRef}
            className={`relative mb-24 block  ${styles.CareerAnimation}`}
          >
            <div className="w-full h-full relative">
              {data?.Card?.map((item: any, i: number) => {
                const isImageBg = i % 2 === 0;
                const isFourth = i % 3 === 0;
                const imageUrl = item?.image?.data?.attributes?.url
                  ? toWebp(`${baseUrl}${item?.image?.data?.attributes?.url}`)
                  : "";

                return (
                  <div
                    key={"card" + (i + 1)}
                    className={`card${i + 1} absolute w-full h-auto min-h-[528px] ${styles[`card${i + 1}`]}`}
                  >
                    <div
                      className={`animated-scroll-1 relative overflow-hidden mb-[60px] min-h-[420px] xl:min-h-[528px] 
                        ${isImageBg
                          ? "shadow-[0px_16px_72px_-10px_rgba(0,26,45,0.24)]"
                          : `${!isFourth ? "bg-[#E6F3FD]" : "bg-[#F3F8E6]"
                          } before:content-[''] before:absolute before:h-[100%] before:w-[100%] before:right[0] before:bottom-[0] before:bg-no-repeat before:bg-[length:100%_100%] before:bg-center before:bg-[#F0F8FF] before:-z-[2]`
                        }`}
                    >
                      {isImageBg ? (
                        <>
                          <Image
                            src={imageUrl}
                            alt={item?.title}
                            width={705}
                            height={528}
                            className="object-cover w-full h-[420px] xl:h-[528px]"
                          />
                          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/20"></div>
                        </>
                      ) : (
                        <div>
                          <div className="absolute left-0 bottom-0 h-full w-full">
                            <Image
                              src={`/images/career-bg-shape-${!isFourth ? 2 : 4
                                }.png`}
                              alt="promise shape"
                              width={191}
                              height={94}
                              className="object-cover w-full h-full "
                            />
                          </div>
                          <div className="absolute right-0 bottom-0">
                            <Image
                              src={`/images/career-bg-shape-${!isFourth ? 3 : 5
                                }.svg`}
                              alt="promise shape"
                              width={191}
                              height={94}
                              className="object-cover w-auto h-auto"
                            />
                          </div>
                        </div>
                      )}

                      {/* Corner dots */}
                      {[
                        "top-[12px] left-[12px]",
                        "top-[12px] right-[12px]",
                        "bottom-[12px] left-[12px]",
                        "bottom-[12px] right-[12px]",
                      ].map((pos, idx) => (
                        <span
                          key={idx}
                          className={`absolute ${pos} z-10 shadow-[0_3px_6px_0_#00000033] bg-white rounded-full w-[10px] h-[10px]`}
                        />
                      ))}

                      {/* Text Content */}
                      <div
                        className={
                          isImageBg
                            ? "absolute top-0 md:p-[32px_24px] p-[40px_12px] z-1"
                            : "md:p-[32px_24px] p-[40px_12px] z-1"
                        }
                      >
                        <h2
                          className={`line-clamp-1 truncate whitespace-nowrap xl:text-[32px] xl:leading-[41.6px] md:text-[24px] md:leading-[31.2px] text-[22px] leading-[28.6px] pb-[16px] md:pb-[24px] font-[700] ${!isImageBg ? " text-[#040405]" : " text-white"
                            }`}
                        >
                          {item?.title}
                        </h2>
                        <div
                          className={`lg:text-[18px] lg:leading-[25.2px] text-[16px] leading-[22.4px] mb-[16px] font-[600] ${isImageBg ? "text-white" : "text-[#32343B]"
                            }`}
                        >
                          {parseHtml(item?.text)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Version */}
        <PromisesMobile data={data} />
      </div>
    </section>
  );
};

export default CareerPromises;
